// import { Menu } from '../service/menu/types';
import { globalStore } from './globalStore';

/**
 * 通用权限检查工具类
 * 支持基于菜单权限的功能控制
 */
export class PermissionManager {
  /**
   * 递归检查菜单权限
   * @param menu 菜单项
   * @param permission 权限标识
   * @returns 是否具有权限
   */
  private static checkMenuPermission(menu: any, permission: string): boolean {
    // 检查当前菜单的权限 - 只检查 meta.perms 字段
    if (menu.meta?.perms === permission) {
      return true;
    }
    
    // 递归检查子菜单
    if (menu.children && menu.children.length > 0) {
      return menu.children.some((child: any) => this.checkMenuPermission(child, permission));
    }
    
    return false;
  }

  /**
   * 检查用户是否具有指定的权限
   * @param permission 权限标识
   * @returns 是否具有权限
   */
  static hasPermission(permission: string): boolean {
    // 首先检查 permissions 数组（从 getMenuPermissionsApi 接口响应的 data.permissions 中获取）
    const permissions = globalStore.getPermissions();
    if (permissions && permissions.length > 0 && permissions.includes(permission)) {
      return true;
    }

    // 如果 permissions 数组中没有找到，再检查 menus 中的 meta.perms
    const menuList = globalStore.getMenuList();
    
    // 遍历菜单列表，检查是否有匹配的权限
    return menuList.some(menu => this.checkMenuPermission(menu, permission));
  }

  /**
   * 检查用户是否具有多个权限中的任意一个
   * @param permissions 权限列表
   * @returns 是否具有任意一个权限
   */
  static hasAnyPermission(permissions: string[]): boolean {
    return permissions.some(permission => this.hasPermission(permission));
  }

  /**
   * 检查用户是否具有所有指定权限
   * @param permissions 权限列表
   * @returns 是否具有所有权限
   */
  static hasAllPermissions(permissions: string[]): boolean {
    return permissions.every(permission => this.hasPermission(permission));
  }

  /**
   * 递归提取菜单权限
   * @param menu 菜单项
   * @param permissions 权限列表
   */
  private static extractPermissions(menu: any, permissions: string[]): void {
    // 添加当前菜单的权限（使用title作为权限标识）
    if (menu.meta?.title) {
      permissions.push(menu.meta.title);
    }
    
    // 递归处理子菜单
    if (menu.children && menu.children.length > 0) {
      menu.children.forEach((child: any) => {
        this.extractPermissions(child, permissions);
      });
    }
  }

  /**
   * 获取用户的所有菜单权限列表
   * @returns 权限列表
   */
  static getAllPermissions(): string[] {
    const menuList = globalStore.getMenuList();
    const permissions: string[] = [];
    
    menuList.forEach(menu => {
      this.extractPermissions(menu, permissions);
    });
    
    return [...new Set(permissions)]; // 去重
  }

  /**
   * 检查用户是否具有指定权限组中的任意一个权限
   * @param permissionGroup 权限组对象
   * @returns 是否具有任意一个权限
   */
  static hasAnyPermissionFromGroup(permissionGroup: Record<string, string>): boolean {
    const permissions = Object.values(permissionGroup);
    return this.hasAnyPermission(permissions);
  }

  /**
   * 检查用户是否具有指定权限组中的所有权限
   * @param permissionGroup 权限组对象
   * @returns 是否具有所有权限
   */
  static hasAllPermissionsFromGroup(permissionGroup: Record<string, string>): boolean {
    const permissions = Object.values(permissionGroup);
    return this.hasAllPermissions(permissions);
  }

  /**
   * 获取用户具有的权限列表（从指定权限组中筛选）
   * @param permissionGroup 权限组对象
   * @returns 用户具有的权限列表
   */
  static getGrantedPermissionsFromGroup(permissionGroup: Record<string, string>): string[] {
    const permissions = Object.values(permissionGroup);
    return permissions.filter(permission => this.hasPermission(permission));
  }

  /**
   * 检查用户是否具有管理员权限
   * @returns 是否具有管理员权限
   */
  static isAdmin(): boolean {
    // 检查是否具有管理员相关的菜单权限
    return this.hasPermission('系统管理') || this.hasPermission('用户管理') || this.hasPermission('菜单管理');
  }

  /**
   * 检查用户是否具有审核员权限
   * @returns 是否具有审核员权限
   */
  static isAssessor(): boolean {
    // 检查是否具有审核相关的菜单权限
    return this.hasPermission('内容审核') || this.hasPermission('审核管理');
  }

  /**
   * 检查用户是否具有操作员权限（用户权限）
   * @returns 是否具有操作员权限
   */
  static isOperator(): boolean {
    // 检查是否具有基础操作权限
    return this.hasPermission('个人中心菜单栏') || this.hasPermission('团队管理');
  }
}
